import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getToken, getUser } from "../../utils/auth";
import { jwtDecode } from "jwt-decode";
import CONFIG from '../../config';

// Helper to submit objection for a hostname owner
const submitObjection = async (userId) => {
    const reason = window.prompt('Please provide a reason for your objection:');
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
        alert('Reason is required.');
        return;
    }
    try {
        const token = getToken();
        const res = await axios.post(`${CONFIG.API_URL}/api/admin/data-subject/objection`, { userId, reason }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        // Show a single alert based on response
        if (res.data && (res.data.success || res.data.message)) {
            alert('Objection recorded successfully.');
        } else {
            alert('Failed to record objection.');
        }
    } catch (err) {
        console.error('Failed to record objection:', err);
        alert('Failed to record objection.');
    }
};

const BreachReportAdmin = () => {
    const { t } = useTranslation();
    const [permissions, setPermissions] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isMaster, setIsMaster] = useState(false);
    const [breaches, setBreaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [restricted, setRestricted] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        severity: 'medium',
        affected_subjects_approx: 0,
        discovery_at: new Date().toISOString().split('T')[0]
    });

    const token = localStorage.getItem('token');

    // Fetch breaches wrapped in useCallback to stabilize reference
    const fetchBreaches = useCallback(async () => {
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/compliance/breaches`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBreaches(res.data);
            setRestricted(false);
        } catch (error) {
            console.error("Error fetching breaches:", error);
            if (error.response?.status === 403 && error.response?.data?.restricted) {
                setRestricted(true);
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchBreaches();
    }, [fetchBreaches]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${CONFIG.API_URL}/api/admin/compliance/breaches`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowForm(false);
            fetchBreaches();
            setFormData({ title: '', description: '', severity: 'medium', affected_subjects_approx: 0, discovery_at: new Date().toISOString().split('T')[0] });
        } catch (error) {
            console.error("Error logging breach:", error);
        }
    };

    // Process breach object
    const handleProcess = async (breachId) => {
        try {
            const token = getToken();
            await axios.post(`${CONFIG.API_URL}/api/admin/compliance/breaches/${breachId}/process`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Processing initiated successfully.');
            fetchBreaches();
        } catch (error) {
            console.error('Error processing breach:', error);
            alert('Failed to initiate processing.');
        }
    };

    // Submit objection to processing for a breach owner using the shared helper
    const handleObjection = async (breachId) => {
        const breach = breaches.find(b => b.id === breachId);
        if (!breach) return;
        // Reuse the submitObjection helper to prompt for reason and send request
        await submitObjection(breach.profile_id);
        // Refresh list after objection
        fetchBreaches();
    };




    useEffect(() => {
        const MASTER_PROFILE_ID = 'prof_01JK0000000000000000000001';
        const computeIsMaster = (data) => {
            const perms = data.permissions || [];
            const hasStar = Array.isArray(perms) ? perms.includes('*') : perms === '*';
            return hasStar && (!data.profile_id || data.profile_id === MASTER_PROFILE_ID);
        };

        const checkPerms = async () => {
            const token = getToken();
            if (!token) return;
            try {
                // Fetch fresh permissions from the dedicated /me endpoint
                const res = await axios.get(`${CONFIG.API_URL}/api/admin/users/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.data) {
                    const master = computeIsMaster(res.data);
                    setIsAdmin(!!res.data.isAdmin);
                    setIsMaster(master);
                    setPermissions(res.data.permissions || []);

                    // Sync with localStorage so ProtectedRoute & Header stay up to date
                    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
                    localStorage.setItem("user", JSON.stringify({
                        ...localUser,
                        isAdmin: !!res.data.isAdmin,
                        isMaster: master,
                        permissions: res.data.permissions || []
                    }));
                    // Notify Header (and any other listener) about the updated user
                    window.dispatchEvent(new Event('user_changed'));
                }
            } catch (err) {
                console.error("Failed to fetch fresh permissions:", err);
                // Fallback to token if /me fails
                try {
                    const decoded = jwtDecode(token);
                    setIsAdmin(!!decoded.isAdmin);
                } catch (e) { }
            } finally {
                setLoading(false);
            }
        };
        checkPerms();
    }, []);

    const hasPerm = (p) => {
        if (loading) return false;
        if (!isAdmin) return false;
        if (permissions.includes('*') || permissions === '*') return true;
        return permissions.includes(p);
    };

    // const canViewBreachReport = hasPerm('*');



    useEffect(() => {
        const check = () => {
            try {
                const u = getUser();
                // setUserState(u);
                setIsMaster(!!u?.isMaster);
            } catch (e) {
                // setUserState(null);
                setIsMaster(false);
            }
        };

        check();

        // const onStorage = (e) => {
        //   if (e.key === 'user') check();
        // };

        const onCustom = () => check();

        // window.addEventListener('storage', onStorage);
        window.addEventListener('user_changed', onCustom);
        return () => {
            // window.removeEventListener('storage', onStorage);
            window.removeEventListener('user_changed', onCustom);
        };
    }, []);

    return (
        <div className="p-6">
            {restricted ? (
                <div className="w-full bg-red-50 text-red-700 p-8 rounded-2xl shadow-xl border border-red-200 text-center mb-8">
                    <i className="fas fa-lock text-5xl mb-4"></i>
                    <h2 className="text-3xl font-black mb-2">{t('admin.access_restricted', 'Access Restricted')}</h2>
                    <p>{t('admin.processing_restricted_msg', 'Your data processing privileges have been restricted or you have formally objected to processing. You cannot access sensitive compliance logs.')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h1 className="text-3xl font-black">{t('admin.breach_logs', 'Data Breach Logs')}</h1>
                            <p className="text-gray-500 mt-1">{t('admin.breach_subtitle', '72-Hour Transparency Compliance Audit Trail')}</p>
                        </div>
                        <button
                            onClick={() => setShowForm(!showForm)}
                            className="bg-red-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-red-700 transition-all flex items-center gap-2"
                        >
                            <i className={`fas ${showForm ? 'fa-times' : 'fa-plus'}`}></i>
                            {showForm ? t('common.cancel', 'Cancel') : t('admin.report_incident', 'Report New Incident')}
                        </button>
                    </div>

                    {showForm && (
                        <div className="bg-white p-8 rounded-2xl shadow-xl border border-red-100 mb-8 animate-slide-up">
                            <h2 className="text-xl font-bold text-red-700 mb-6 flex items-center gap-2">
                                <i className="fas fa-exclamation-triangle"></i>
                                {t('admin.incident_details', 'Incident Details')}
                            </h2>
                            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold uppercase tracking-wider mb-2">{t('admin.incident_title', 'Incident Title')}</label>
                                    <input
                                        type="text"
                                        className="w-full p-4 rounded-xl border border-gray-200"
                                        required
                                        value={formData.title}
                                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                                        placeholder="e.g., Potential database access anomaly detected"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold uppercase tracking-wider mb-2">{t('admin.incident_desc', 'Description & Findings')}</label>
                                    <textarea
                                        className="w-full p-4 rounded-xl border border-gray-200"
                                        rows="4"
                                        required
                                        value={formData.description}
                                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Detail the nature of the breach, affected systems, and mitigation steps taken."
                                    ></textarea>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold uppercase tracking-wider mb-2">{t('admin.severity', 'Severity Level')}</label>
                                    <select
                                        className="w-full p-4 rounded-xl border border-gray-200 appearance-none bg-white"
                                        value={formData.severity}
                                        onChange={e => setFormData({ ...formData, severity: e.target.value })}
                                    >
                                        <option value="low">Low (Non-PII)</option>
                                        <option value="medium">Medium (Potential PII Exposure)</option>
                                        <option value="high">High (Known PII Exposure)</option>
                                        <option value="critical">Critical (Compromised Systems)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold uppercase tracking-wider mb-2">{t('admin.affected_subjects', 'Approx. Affected Subjects')}</label>
                                    <input
                                        type="number"
                                        className="w-full p-4 rounded-xl border border-gray-200"
                                        value={formData.affected_subjects_approx}
                                        onChange={e => setFormData({ ...formData, affected_subjects_approx: parseInt(e.target.value) })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold uppercase tracking-wider mb-2">{t('admin.discovery_date', 'Discovery Date & Time')}</label>
                                    <input
                                        type="datetime-local"
                                        className="w-full p-4 rounded-xl border border-gray-200"
                                        required
                                        value={formData.discovery_at}
                                        onChange={e => setFormData({ ...formData, discovery_at: e.target.value })}
                                    />
                                </div>
                                <div className="flex items-end">
                                    <button type="submit" className="w-full bg-gray-900 text-white p-4 rounded-xl font-bold uppercase tracking-widest hover:bg-red-600 transition-all">
                                        {t('admin.save_report', 'Save & Log Incident')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="p-5 font-bold uppercase text-[10px] tracking-widest text-gray-400">Incident</th>
                                    <th className="p-5 font-bold uppercase text-[10px] tracking-widest text-gray-400">Severity</th>
                                    <th className="p-5 font-bold uppercase text-[10px] tracking-widest text-gray-400">Status</th>
                                    <th className="p-5 font-bold uppercase text-[10px] tracking-widest text-gray-400">Subjects</th>
                                    <th className="p-5 font-bold uppercase text-[10px] tracking-widest text-gray-400">Discovery</th>
                                    <th className="p-5 font-bold uppercase text-[10px] tracking-widest text-gray-400">Hostname</th>
                                    <th className="p-5 font-bold uppercase text-[10px] tracking-widest text-gray-400">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {breaches.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan="5" className="p-12 text-center text-gray-400 font-medium">
                                            {t('admin.no_breaches', 'No security incidents recorded. Performance is within compliance standards.')}
                                        </td>
                                    </tr>
                                )}
                                {breaches.map(b => (
                                    isMaster &&
                                    (
                                        // <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                                        //     <td className="p-5">
                                        //         <div className="font-bold text-gray-900">{b.title}</div>
                                        //         <div className="text-xs text-gray-400 mt-1 line-clamp-1">{b.description}</div>
                                        //     </td>
                                        //     <td className="p-5">
                                        //         <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${b.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                        //             b.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                        //                 b.severity === 'medium' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                        //             }`}>
                                        //             {b.severity}
                                        //         </span>
                                        //     </td>
                                        //     <td className="p-5">
                                        //         <span className="text-sm font-medium capitalize text-gray-600">{b.status.replace('_', ' ')}</span>
                                        //     </td>
                                        //     <td className="p-5 font-mono text-sm text-gray-500">
                                        //         {b.affected_subjects_approx || 0}
                                        //     </td>
                                        //     <td className="p-5 text-sm text-gray-400">
                                        //         {new Date(b.discovery_at).toLocaleDateString()}
                                        //     </td>
                                        // </tr>
                                        <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="p-5">
                                                <div className="font-bold text-gray-900">{b.title}</div>
                                                <div className="text-xs text-gray-400 mt-1 line-clamp-1">{b.description}</div>
                                            </td>
                                            <td className="p-5">
                                                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${b.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                                    b.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                                        b.severity === 'medium' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {b.severity}
                                                </span>
                                            </td>
                                            <td className="p-5">
                                                <span className="text-sm font-medium capitalize text-gray-600">{b.status.replace('_', ' ')}</span>
                                            </td>
                                            <td className="p-5 font-mono text-sm text-gray-500">
                                                {b.affected_subjects_approx || 0}
                                            </td>
                                            <td className="p-5 text-sm text-gray-400">
                                                {new Date(b.discovery_at).toLocaleDateString()}
                                            </td>
                                            <td className="p-5 text-sm text-gray-600">
                                                {b.hostname || '—'}
                                            </td>
                                            <td className="p-5 text-sm text-gray-600 flex gap-2">
                                                <button onClick={() => handleProcess(b.id)} className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition">
                                                    Process
                                                </button>
                                                <button onClick={() => handleObjection(b.id)} className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition">
                                                    Object to Processing
                                                </button>
                                            </td>
                                        </tr>
                                    ) &&
                                    // ||
                                    // canViewBreachReport
                                    hasPerm && (
                                        <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="p-5">
                                                <div className="font-bold text-gray-900">{b.title}</div>
                                                <div className="text-xs text-gray-400 mt-1 line-clamp-1">{b.description}</div>
                                            </td>
                                            <td className="p-5">
                                                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${b.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                                    b.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                                        b.severity === 'medium' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {b.severity}
                                                </span>
                                            </td>
                                            <td className="p-5">
                                                <span className="text-sm font-medium capitalize text-gray-600">{b.status.replace('_', ' ')}</span>
                                            </td>
                                            <td className="p-5 font-mono text-sm text-gray-500">
                                                {b.affected_subjects_approx || 0}
                                            </td>
                                            <td className="p-5 text-sm text-gray-400">
                                                {new Date(b.discovery_at).toLocaleDateString()}
                                            </td>
                                            <td className="p-5 text-sm text-gray-600">
                                                {b.hostname || '—'}
                                            </td>
                                            <td className="p-5 text-sm text-gray-600 flex gap-2">
                                                <button onClick={() => handleProcess(b.id)} className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition">
                                                    Process
                                                </button>
                                                <button onClick={() => handleObjection(b.id)} className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition">
                                                    Object to Processing
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-8 p-6 bg-blue-50 rounded-2xl border border-blue-100">
                        <h4 className="text-blue-800 font-bold mb-2 flex items-center gap-2">
                            <i className="fas fa-info-circle"></i>
                            72-Hour Reporting Reminder
                        </h4>
                        <p className="text-sm text-blue-700 leading-relaxed">
                            Under **GDPR Art. 33**, **Saudi PDPL**, and **UAE PDPL**, personal data breaches must be reported to the relevant authority within **72 hours** of discovery. Logging the incident here creates a verifiable audit trail of your "awareness" time.
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

export default BreachReportAdmin;
